import { SWISS_UID_REGEX } from '../utils/constants';
import { FormattedUID } from './swissEnterpriseRegisterApi.types';

/**
 * Formats UID input for API call
 * Extracts CHE prefix and 9-digit ID from various formats
 */
export const formatUIDForAPI = (uid: string): FormattedUID | null => {
  if (!uid) {
    return null;
  }

  const cleaned = uid.trim().replace(/\s/g, '');

  let match: RegExpMatchArray | null = null;

  if (SWISS_UID_REGEX.FORMATTED.test(cleaned)) {
    // CHE-123.456.789
    match = cleaned.match(/^CHE-(\d{3})\.(\d{3})\.(\d{3})$/);
  } else if (SWISS_UID_REGEX.NO_SEPARATORS.test(cleaned)) {
    // CHE123456789
    match = cleaned.match(/^CHE(\d{9})$/);
  } else if (SWISS_UID_REGEX.NUMERIC_WITH_DOTS.test(cleaned)) {
    // 123.456.789
    match = cleaned.match(/^(\d{3})\.(\d{3})\.(\d{3})$/);
  } else if (SWISS_UID_REGEX.NUMERIC_ONLY.test(cleaned)) {
    // 123456789
    match = cleaned.match(/^(\d{9})$/);
  }

  if (!match) {
    return null;
  }

  const digits = match.slice(1).join('').replace(/\./g, '');

  if (digits.length !== 9) {
    return null;
  }

  return {
    category: 'CHE',
    id: digits,
  };
};

/**
 * Validates UID format
 */
export const isValidUIDFormat = (uid: string): boolean => {
  if (!uid) {
    return false;
  }

  const cleaned = uid.trim().replace(/\s/g, '');

  return (
    SWISS_UID_REGEX.FORMATTED.test(cleaned)
    || SWISS_UID_REGEX.NO_SEPARATORS.test(cleaned)
    || SWISS_UID_REGEX.NUMERIC_WITH_DOTS.test(cleaned)
    || SWISS_UID_REGEX.NUMERIC_ONLY.test(cleaned)
  );
};

/**
 * Formats a UID to the official display format (CHE-XXX.XXX.XXX)
 */
export const formatUIDForDisplay = (uid: string): string => {
  const formatted = formatUIDForAPI(uid);
  if (!formatted) {
    return uid;
  }

  const { id } = formatted;
  return `CHE-${id.slice(0, 3)}.${id.slice(3, 6)}.${id.slice(6, 9)}`;
};
